/**
 * Product Details — Image Reveal
 *
 * Desktop (≥768px): cursor X-position controls a clip-path on the top image.
 *   Moving cursor RIGHT → reveals more of the bottom image from the right.
 *   Moving cursor LEFT  → covers back up.
 *
 * Mobile (<768px): automatic wipe animation that loops:
 *   fully covered → wipe reveal → pause → wipe back → pause → repeat
 *
 * Also handles smooth slide animation for <details> dropdowns.
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 768;

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  /* ══════════════════════════════════════════════════════
     Desktop: auto-wipe (same loop as mobile) +
              mouse-hover overrides while cursor is over the container
     ══════════════════════════════════════════════════════ */
  function initDesktop(container, topLayer) {
    var WIPE_DURATION  = 1000;  // ms to wipe from 0→100 or 100→0
    var PAUSE_REVEALED = 2000;  // ms to hold the revealed state
    var PAUSE_COVERED  = 2500;  // ms to hold the covered state
    var LERP           = 0.12;  // hover lerp factor per frame (~60fps feels ~100ms ease-in)

    var animFrame  = null;
    var timeout    = null;
    var running    = true;   // auto-wipe active
    var isHovering = false;
    var displayPct = 0;      // the value currently rendered
    var mousePct   = 0;      // latest mouse-mapped target

    function setClip(pct) {
      displayPct = pct;
      topLayer.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
    }

    /* ── Auto-wipe eased animation ── */
    function animate(from, to, duration, cb) {
      var start = null;
      function step(ts) {
        if (!running || isHovering) return;
        if (!start) start = ts;
        var elapsed  = ts - start;
        var progress = Math.min(elapsed / duration, 1);
        var eased    = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        setClip(from + (to - from) * eased);
        if (progress < 1) {
          animFrame = requestAnimationFrame(step);
        } else {
          setClip(to);
          if (cb) cb();
        }
      }
      animFrame = requestAnimationFrame(step);
    }

    /* ── Auto-wipe loop ── */
    function cycle() {
      if (!running || isHovering) return;
      animate(0, 100, WIPE_DURATION, function () {
        if (!running || isHovering) return;
        container.classList.add('is-revealing');
        timeout = setTimeout(function () {
          if (!running || isHovering) return;
          animate(100, 0, WIPE_DURATION, function () {
            if (!running || isHovering) return;
            container.classList.remove('is-revealing');
            timeout = setTimeout(function () {
              if (running && !isHovering) cycle();
            }, PAUSE_COVERED);
          });
        }, PAUSE_REVEALED);
      });
    }

    /* ── Lerp loop (runs only while hovering) ── */
    function lerpLoop() {
      if (!isHovering) return;
      var diff = mousePct - displayPct;
      // Snap when close enough to avoid infinite tiny steps
      setClip(Math.abs(diff) < 0.05 ? mousePct : displayPct + diff * LERP);
      container.classList.toggle('is-revealing', displayPct > 50);
      animFrame = requestAnimationFrame(lerpLoop);
    }

    /* ── Mouse handlers ── */
    function onPointerMove(e) {
      if (!isHovering) return;
      var rect = container.getBoundingClientRect();
      mousePct = Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100));
    }

    function onMouseEnter(e) {
      isHovering = true;
      // Stop auto-wipe
      running = false;
      cancelAnimationFrame(animFrame);
      clearTimeout(timeout);
      animFrame = null;
      timeout   = null;
      // Seed mouse position so lerp starts aimed the right direction immediately
      var rect = container.getBoundingClientRect();
      mousePct = Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100));
      // Lerp loop takes over — starts from displayPct, chases mousePct each frame
      lerpLoop();
    }

    /* ── Resume auto-wipe from an arbitrary position ──
       If pct > 50: finish revealing (→100), pause, wipe back, loop.
       If pct ≤ 50: wipe back to covered (→0), pause, then loop normally.
       Duration is proportional so the wipe speed stays consistent. */
    function resumeFromPosition(pct) {
      if (!running || isHovering) return;

      if (pct > 50) {
        // Finish the reveal
        var revealDuration = ((100 - pct) / 100) * WIPE_DURATION;
        animate(pct, 100, revealDuration, function () {
          if (!running || isHovering) return;
          container.classList.add('is-revealing');
          timeout = setTimeout(function () {
            if (!running || isHovering) return;
            animate(100, 0, WIPE_DURATION, function () {
              if (!running || isHovering) return;
              container.classList.remove('is-revealing');
              timeout = setTimeout(function () {
                if (running && !isHovering) cycle();
              }, PAUSE_COVERED);
            });
          }, PAUSE_REVEALED);
        });
      } else {
        // Wipe back to covered, then start the normal loop
        var coverDuration = (pct / 100) * WIPE_DURATION;
        animate(pct, 0, coverDuration, function () {
          if (!running || isHovering) return;
          container.classList.remove('is-revealing');
          timeout = setTimeout(function () {
            if (running && !isHovering) cycle();
          }, PAUSE_COVERED);
        });
      }
    }

    function onMouseLeave() {
      isHovering = false;
      cancelAnimationFrame(animFrame);
      animFrame = null;
      container.classList.toggle('is-revealing', displayPct > 50);
      running = true;
      resumeFromPosition(displayPct);
    }

    // Initial state — fully covered, then start auto-wipe
    setClip(0);
    timeout = setTimeout(cycle, PAUSE_COVERED);

    document.addEventListener('mousemove', onPointerMove);
    container.addEventListener('mouseenter', onMouseEnter);
    container.addEventListener('mouseleave', onMouseLeave);

    return {
      destroy: function () {
        running = false;
        isHovering = false;
        cancelAnimationFrame(animFrame);
        clearTimeout(timeout);
        document.removeEventListener('mousemove', onPointerMove);
        container.removeEventListener('mouseenter', onMouseEnter);
        container.removeEventListener('mouseleave', onMouseLeave);
      }
    };
  }

  /* ══════════════════════════════════════════════════════
     Mobile: auto-wipe looping animation
     ══════════════════════════════════════════════════════ */
  function initMobile(container, topLayer) {
    var WIPE_DURATION = 1000;  // ms to wipe from 0→100 or 100→0
    var PAUSE_REVEALED = 2000; // ms to hold the revealed state
    var PAUSE_COVERED  = 2500; // ms to hold the covered state

    var animFrame = null;
    var timeout = null;
    var running = true;

    function updateClip(pct) {
      // pct 0 = fully covered (top layer visible), 100 = fully revealed
      topLayer.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
    }

    function animate(from, to, duration, cb) {
      var start = null;
      function step(ts) {
        if (!running) return;
        if (!start) start = ts;
        var elapsed = ts - start;
        var progress = Math.min(elapsed / duration, 1);
        // Ease in-out for a smooth feel
        var eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        var val = from + (to - from) * eased;
        updateClip(val);
        if (progress < 1) {
          animFrame = requestAnimationFrame(step);
        } else {
          updateClip(to);
          if (cb) cb();
        }
      }
      animFrame = requestAnimationFrame(step);
    }

    function cycle() {
      if (!running) return;
      // Wipe forward (reveal)
      animate(0, 100, WIPE_DURATION, function () {
        container.classList.add('is-revealing');
        // Pause revealed
        timeout = setTimeout(function () {
          if (!running) return;
          // Wipe back (cover)
          animate(100, 0, WIPE_DURATION, function () {
            container.classList.remove('is-revealing');
            // Pause covered, then loop
            timeout = setTimeout(function () {
              if (running) cycle();
            }, PAUSE_COVERED);
          });
        }, PAUSE_REVEALED);
      });
    }

    // Start with a brief delay so the page settles
    updateClip(0);
    timeout = setTimeout(cycle, PAUSE_COVERED);

    return {
      destroy: function () {
        running = false;
        cancelAnimationFrame(animFrame);
        clearTimeout(timeout);
      }
    };
  }

  /* ══════════════════════════════════════════════════════
     Init / resize handling
     ══════════════════════════════════════════════════════ */
  function initReveal(container) {
    var topLayer = container.querySelector('[data-reveal-top]');
    if (!topLayer) return;

    var currentMode = null; // 'desktop' | 'mobile'
    var currentInstance = null;

    function setup() {
      var mode = isMobile() ? 'mobile' : 'desktop';
      if (mode === currentMode) return;

      // Tear down previous
      if (currentInstance) {
        currentInstance.destroy();
        currentInstance = null;
      }

      // Reset clip
      topLayer.style.clipPath = '';
      container.classList.remove('is-revealing');

      // Boot new mode
      if (mode === 'desktop') {
        currentInstance = initDesktop(container, topLayer);
      } else {
        currentInstance = initMobile(container, topLayer);
      }
      currentMode = mode;
    }

    setup();

    // Re-evaluate on resize (debounced)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setup, 150);
    });
  }

  function init() {
    var containers = document.querySelectorAll('[data-image-reveal]');
    containers.forEach(initReveal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-init
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var containers = e.target.querySelectorAll('[data-image-reveal]');
      containers.forEach(initReveal);
      // Re-init dropdown animations for newly loaded sections
      initDropdownAnimations(e.target);
    });
  }

  /* ══════════════════════════════════════════════════════
     Smooth slide animation for <details> dropdowns
     ══════════════════════════════════════════════════════ */

  // Animate-close a single <details> element (reusable)
  function closeDropdown(el) {
    if (!el.open || el.classList.contains('is-closing')) return;

    var body = el.classList.contains('pdr__faq-item')
      ? el.querySelector('.pdr__faq-answer')
      : el.querySelector('.pdr__dropdown-body');

    if (!body) { el.open = false; return; }

    el.classList.add('is-closing');

    function onEnd() {
      body.removeEventListener('transitionend', onEnd);
      el.open = false;
      el.classList.remove('is-closing');
      el._pdrAnimating = false;
    }

    body.addEventListener('transitionend', onEnd);

    setTimeout(function () {
      if (el.classList.contains('is-closing')) {
        el.open = false;
        el.classList.remove('is-closing');
        el._pdrAnimating = false;
      }
    }, 500);
  }

  function initDropdownAnimations(root) {
    var details = root.querySelectorAll('.pdr__dropdown, .pdr__faq-item');

    details.forEach(function (el) {
      // Skip if already initialised
      if (el._pdrAnimInit) return;
      el._pdrAnimInit = true;

      var summary = el.querySelector('summary');
      if (!summary) return;

      // The animated body container (the grid wrapper)
      var body = el.classList.contains('pdr__faq-item')
        ? el.querySelector('.pdr__faq-answer')
        : el.querySelector('.pdr__dropdown-body');

      if (!body) return;

      var isAnimating = false;

      summary.addEventListener('click', function (e) {
        e.preventDefault();

        if (isAnimating) return;

        if (el.open) {
          // ── CLOSING ──
          isAnimating = true;
          el.classList.add('is-closing');

          function onEnd() {
            body.removeEventListener('transitionend', onEnd);
            el.open = false;
            el.classList.remove('is-closing');
            isAnimating = false;
          }

          body.addEventListener('transitionend', onEnd);

          setTimeout(function () {
            if (isAnimating) {
              el.open = false;
              el.classList.remove('is-closing');
              isAnimating = false;
            }
          }, 500);
        } else {
          // ── OPENING ──
          // Close all open siblings first
          var isFaqItem = el.classList.contains('pdr__faq-item');
          var siblingSelector = isFaqItem ? '.pdr__faq-item' : '.pdr__dropdown';
          // Siblings share the same parent container
          var parent = isFaqItem ? el.closest('.pdr__dropdown-inner') : el.closest('.pdr');
          if (parent) {
            parent.querySelectorAll(siblingSelector).forEach(function (sibling) {
              if (sibling !== el) closeDropdown(sibling);
            });
          }

          // Explicitly set the collapsed state so the browser has a "from" value
          body.style.gridTemplateRows = '0fr';
          el.open = true;
          // eslint-disable-next-line no-unused-expressions
          body.offsetHeight;
          body.style.gridTemplateRows = '';
        }
      });
    });
  }

  // Init dropdown animations on page load
  function initDropdowns() {
    var sections = document.querySelectorAll('.pdr');
    sections.forEach(function (section) {
      initDropdownAnimations(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDropdowns);
  } else {
    initDropdowns();
  }
})();
